// Light or dark, for the dashboard and the marketing pages.
//
// Nobody has to pick: until someone presses the switch we follow their
// operating system, and keep following it if they change it while the tab is
// open. Pressing the switch saves the choice in localStorage, and from then on
// it wins over the OS.
//
// The class this puts on <html> is all the styling needs; tailwind.config.js
// swaps the whole palette under `.dark`. index.html repeats the first-paint
// half of this inline, so a dark-mode visitor does not see a white flash
// before the bundle loads. Keep the key in step with that script.

export type ColorMode = "light" | "dark";

export const COLOR_MODE_KEY = "buildbotsnow.colorMode";

export function savedColorMode(): ColorMode | null {
  try {
    const value = localStorage.getItem(COLOR_MODE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    // Storage blocked (some privacy modes). Fall back to the OS.
    return null;
  }
}

// Guarded for the build, which renders the legal pages to HTML in Node where
// there is no window (see src/prerender.tsx). There it reads as light.
function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function currentColorMode(): ColorMode {
  return savedColorMode() ?? (systemPrefersDark() ? "dark" : "light");
}

export function applyColorMode(mode: ColorMode): void {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

export function saveColorMode(mode: ColorMode): void {
  try {
    localStorage.setItem(COLOR_MODE_KEY, mode);
  } catch {
    // Still applies for this page view; it just will not be remembered.
  }
  applyColorMode(mode);
}

// Calls back when the OS setting changes, but only while the visitor has not
// made a choice of their own. Returns the unsubscribe.
export function followSystemColorMode(onChange: (mode: ColorMode) => void): () => void {
  if (typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const listener = () => {
    if (savedColorMode()) return;
    const mode = query.matches ? "dark" : "light";
    applyColorMode(mode);
    onChange(mode);
  };
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}
